import { useCallback, useEffect, useState } from "react";
import api, { formatApiError, formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
  DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, AlertCircle, Pencil, Lock } from "lucide-react";
import { toast } from "sonner";

const empty = {
  name: "", kind: "product", group: "Primary", unit: "Nos", hsn: "", gst_rate: 18,
  opening_qty: 0, opening_rate: 0, opening_balance_date: "",
  reorder_level: 0, godown: "Main",
};

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

function Inner() {
  const { activeId } = useCompany();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  // `usage = { locked: bool, voucher_count: int }`. Populated when entering
  // Edit mode so we know whether to disable the opening fields.
  const [usage, setUsage] = useState({ locked: false, voucher_count: 0 });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!activeId) return;
    const [I, S] = await Promise.all([
      api.get(`/companies/${activeId}/items`),
      api.get(`/companies/${activeId}/stock-summary`),
    ]);
    setItems(I.data);
    setSummary(S.data);
  }, [activeId]);

  useEffect(() => { load(); }, [load]);

  const startCreate = () => {
    setEditing(null);
    setUsage({ locked: false, voucher_count: 0 });
    setForm({ ...empty, opening_balance_date: todayISO() });
    setOpen(true);
  };

  const startEdit = async (item) => {
    setEditing(item);
    setForm({
      name: item.name, kind: item.kind || "product",
      group: item.group || "Primary", unit: item.unit || "Nos",
      hsn: item.hsn || "", gst_rate: item.gst_rate ?? 0,
      opening_qty: item.opening_qty ?? 0, opening_rate: item.opening_rate ?? 0,
      opening_balance_date: item.opening_balance_date || todayISO(),
      reorder_level: item.reorder_level ?? 0, godown: item.godown || "Main",
    });
    // Default to locked while loading so opening fields don't briefly become
    // editable on slow networks.
    setUsage({ locked: true, voucher_count: 0 });
    setOpen(true);
    try {
      const { data } = await api.get(`/items/${item.id}/usage`);
      setUsage(data);
    } catch (e) {
      // If the usage call fails, fall back to a safe "locked" state.
      setUsage({ locked: true, voucher_count: 0 });
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        opening_qty: Number(form.opening_qty) || 0,
        opening_rate: Number(form.opening_rate) || 0,
        reorder_level: Number(form.reorder_level) || 0,
        gst_rate: Number(form.gst_rate) || 0,
        opening_balance_date: (form.opening_balance_date || "").trim() || todayISO(),
      };
      if (editing) {
        await api.put(`/items/${editing.id}`, payload);
        toast.success("Item updated");
      } else {
        await api.post(`/companies/${activeId}/items`, payload);
        toast.success("Item created");
      }
      setOpen(false);
      setForm(empty);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (i) => {
    if (!window.confirm(`Delete item "${i.name}"?`)) return;
    try {
      await api.delete(`/items/${i.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // Once an item has been used in a voucher, the three opening fields become
  // read-only to protect past closing-stock valuations from retroactive change.
  const openingLocked = Boolean(editing && usage.locked);

  return (
    <>
      <PageHeader
        title="Inventory"
        subtitle="Stock items, reorder levels and live stock summary"
        actions={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setUsage({ locked: false, voucher_count: 0 }); } }}>
            <DialogTrigger asChild>
              <Button className="bg-blue-700 hover:bg-blue-800 h-8" data-testid="btn-new-item" onClick={startCreate}>
                <Plus className="w-4 h-4 mr-1" /> New Item
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editing ? `Edit Item — ${editing.name}` : "Create stock item"}</DialogTitle>
                <DialogDescription className="text-xs">
                  {editing
                    ? "Metadata (name, unit, HSN, GST%) can always be edited — past vouchers retain their original values. Opening fields lock once the item is used in any voucher."
                    : "Add a new stock item. Opening qty/rate/date drive the initial FIFO lot for valuations."}
                </DialogDescription>
              </DialogHeader>

              {openingLocked && (
                <div className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="opening-locked-banner">
                  <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>
                    Opening qty / rate / date are <strong>locked</strong> because this item is referenced in{" "}
                    <strong>{usage.voucher_count} voucher{usage.voucher_count === 1 ? "" : "s"}</strong>.
                    To adjust stock, create a <em>Stock Journal</em> voucher dated today.
                  </span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 pt-2">
                <div className="col-span-2 space-y-1">
                  <Label className="text-xs uppercase tracking-wider text-gray-600">Item name *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="input-item-name" />
                </div>

                {/* Product vs Service — services stay OUT of closing-stock
                    valuation. Locked once the item has been used in any
                    voucher (openingLocked implies "in use"). */}
                <div className="col-span-2 space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-gray-600 flex items-center gap-1">
                    Item Type {openingLocked && <Lock className="w-3 h-3 text-amber-600" />}
                  </Label>
                  <div className="flex gap-2" data-testid="radio-item-kind">
                    <button
                      type="button"
                      onClick={() => !openingLocked && setForm({ ...form, kind: "product" })}
                      disabled={openingLocked}
                      className={`flex-1 flex items-start gap-2 border rounded px-3 py-2 text-left transition-colors disabled:opacity-70 disabled:cursor-not-allowed ${
                        form.kind !== "service"
                          ? "border-blue-600 bg-blue-50/60 ring-1 ring-blue-200"
                          : "border-gray-200 hover:border-gray-400"
                      }`}
                      data-testid="radio-item-kind-product"
                    >
                      <div className="mt-0.5 w-3.5 h-3.5 rounded-full border border-gray-400 flex items-center justify-center shrink-0">
                        {form.kind !== "service" && <div className="w-2 h-2 rounded-full bg-blue-600" />}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium">Physical Product</div>
                        <div className="text-[10.5px] text-gray-500 leading-snug">Tangible good — tracked in inventory, counts toward closing stock, FIFO-valued.</div>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => !openingLocked && setForm({ ...form, kind: "service", opening_qty: 0, opening_rate: 0 })}
                      disabled={openingLocked}
                      className={`flex-1 flex items-start gap-2 border rounded px-3 py-2 text-left transition-colors disabled:opacity-70 disabled:cursor-not-allowed ${
                        form.kind === "service"
                          ? "border-purple-600 bg-purple-50/60 ring-1 ring-purple-200"
                          : "border-gray-200 hover:border-gray-400"
                      }`}
                      data-testid="radio-item-kind-service"
                    >
                      <div className="mt-0.5 w-3.5 h-3.5 rounded-full border border-gray-400 flex items-center justify-center shrink-0">
                        {form.kind === "service" && <div className="w-2 h-2 rounded-full bg-purple-600" />}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium">Service</div>
                        <div className="text-[10.5px] text-gray-500 leading-snug">Intangible — not tracked in inventory, excluded from closing-stock calculations. Use SAC (not HSN).</div>
                      </div>
                    </button>
                  </div>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider text-gray-600">Unit</Label>
                  <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} data-testid="input-item-unit" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider text-gray-600">Group</Label>
                  <Input value={form.group} onChange={(e) => setForm({ ...form, group: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider text-gray-600">HSN/SAC</Label>
                  <Input value={form.hsn} onChange={(e) => setForm({ ...form, hsn: e.target.value })} className="font-mono" data-testid="input-item-hsn" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider text-gray-600">GST %</Label>
                  <Input type="number" value={form.gst_rate} onChange={(e) => setForm({ ...form, gst_rate: e.target.value })} data-testid="input-item-gst" />
                </div>

                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider text-gray-600 flex items-center gap-1">
                    Opening Qty {openingLocked && <Lock className="w-3 h-3 text-amber-600" />}
                    {form.kind === "service" && <span className="text-[10px] text-purple-600 normal-case tracking-normal">N/A for services</span>}
                  </Label>
                  <Input
                    type="number"
                    value={form.kind === "service" ? 0 : form.opening_qty}
                    onChange={(e) => setForm({ ...form, opening_qty: e.target.value })}
                    disabled={openingLocked || form.kind === "service"}
                    data-testid="input-item-opening-qty"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider text-gray-600 flex items-center gap-1">
                    Opening Rate (₹) {openingLocked && <Lock className="w-3 h-3 text-amber-600" />}
                    {form.kind === "service" && <span className="text-[10px] text-purple-600 normal-case tracking-normal">N/A for services</span>}
                  </Label>
                  <Input
                    type="number"
                    value={form.kind === "service" ? 0 : form.opening_rate}
                    onChange={(e) => setForm({ ...form, opening_rate: e.target.value })}
                    disabled={openingLocked || form.kind === "service"}
                    data-testid="input-item-opening-rate"
                  />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-xs uppercase tracking-wider text-gray-600 flex items-center gap-1">
                    Opening Balance Date {openingLocked && <Lock className="w-3 h-3 text-amber-600" />}
                    <span className="ml-2 text-[10px] text-gray-400 normal-case tracking-normal">
                      Defaults to today. Gates the FIFO opening lot for date-bounded reports.
                    </span>
                  </Label>
                  <Input
                    type="date"
                    value={form.opening_balance_date || ""}
                    onChange={(e) => setForm({ ...form, opening_balance_date: e.target.value })}
                    disabled={openingLocked || form.kind === "service"}
                    data-testid="input-item-opening-date"
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider text-gray-600">Reorder Level</Label>
                  <Input type="number" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider text-gray-600">Godown</Label>
                  <Input value={form.godown} onChange={(e) => setForm({ ...form, godown: e.target.value })} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} disabled={saving || !form.name} className="bg-blue-700 hover:bg-blue-800" data-testid="btn-save-item">
                  {saving ? "Saving…" : (editing ? "Save Changes" : "Create")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="p-6 space-y-4">
        <div className="border border-gray-200 rounded bg-white overflow-hidden">
          <div className="px-4 py-2 border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wider text-gray-600 font-semibold flex items-center justify-between">
            <span>Stock Summary</span>
            {summary.length > 0 && summary[0].valuation_method && (
              <span
                className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 border border-blue-200 normal-case tracking-normal"
                data-testid="stock-summary-method-badge"
              >
                Valuation: {summary[0].valuation_method.replaceAll("_", " ")}
              </span>
            )}
          </div>
          <table className="tx-table">
            <thead>
              <tr>
                <th>Item</th><th>Unit</th>
                <th className="text-right">Qty on Hand</th>
                <th className="text-right">Rate</th>
                <th className="text-right">Value</th>
                <th className="text-right">Reorder</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {summary.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-sm text-gray-400">No items</td></tr>}
              {summary.map((s) => {
                const item = items.find((i) => i.id === s.id);
                return (
                  <tr key={s.id} data-testid={`row-item-${s.id}`}>
                    <td className="font-medium">
                      <div className="flex items-center gap-1.5">
                        {s.name}
                        {s.kind === "service" && (
                          <span
                            className="text-[9.5px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 uppercase tracking-wider"
                            data-testid={`badge-service-${s.id}`}
                          >
                            Service
                          </span>
                        )}
                      </div>
                      {item?.opening_balance_date && Number(item.opening_qty) !== 0 && (
                        <div className="text-[10px] font-mono text-gray-400 leading-tight">opening as on {item.opening_balance_date}</div>
                      )}
                    </td>
                    <td className="text-xs text-gray-500">{s.unit}</td>
                    <td className="tx-num">{s.kind === "service" ? <span className="text-gray-300">—</span> : formatINR(s.qty)}</td>
                    <td className="tx-num">{s.kind === "service" ? <span className="text-gray-300">—</span> : formatINR(s.rate)}</td>
                    <td className="tx-num">{s.kind === "service" ? <span className="text-gray-300">—</span> : formatINR(s.value)}</td>
                    <td className="tx-num">
                      {s.kind === "service" ? (
                        <span className="text-gray-300">—</span>
                      ) : s.below_reorder ? (
                        <span className="inline-flex items-center gap-1 text-red-700">
                          <AlertCircle className="w-3.5 h-3.5" /> {formatINR(s.reorder_level)}
                        </span>
                      ) : formatINR(s.reorder_level)}
                    </td>
                    <td className="text-right">
                      {item && (
                        <div className="inline-flex items-center gap-1">
                          <button
                            onClick={() => startEdit(item)}
                            className="text-blue-700 hover:text-blue-900"
                            title="Edit item"
                            data-testid={`btn-edit-item-${item.id}`}
                          >
                            <Pencil className="w-4 h-4 inline" />
                          </button>
                          <button
                            onClick={() => remove(item)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete item"
                            data-testid={`btn-delete-item-${item.id}`}
                          >
                            <Trash2 className="w-4 h-4 inline" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export default function Inventory() {
  return <RequireCompany><Inner /></RequireCompany>;
}
